<template>
  <div>
    <b-container>
      <validation-observer v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Detalle de padecimientos / tratamiento</h6>
            </template>

            <p v-if="hayAlgunSi" class="text-muted small mb-3">
              Para la/s persona/s que fueron afirmativas en la pregunta anterior, indique a continuación el detalle de
              la enfermedad y tratamiento que recibe.
            </p>

            <div v-if="hayAlgunSi" class="detalle-por-solicitante">
              <b-card
                v-for="(grupo, gIndex) in detallesPorSolicitante"
                :key="'sol-' + gIndex"
                class="mb-3 detalle-solicitante-card"
              >
                <template #header>
                  <h6 class="mb-0">{{ grupo.nombreSolicitante || "Solicitante" }}</h6>
                </template>
                <div
                  v-for="(item, index) in grupo.items"
                  :key="((item && item.pregunta) || '') + '-' + gIndex + '-' + index"
                  class="mb-3 persona-asegurar-card"
                >
                  <b-card no-body class="border persona-asegurar-inner">
                    <b-card-header
                      class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center 
                             justify-content-between py-2 persona-asegurar-header bg-light"
                    >
                      <div class="pr-sm-2 mb-2 mb-sm-0 flex-grow-1">
                        <span class="font-weight-medium d-block">{{ textoPregunta(item) }}</span>
                      </div>
                      <b-button
                        pill
                        variant="outline-danger"
                        size="sm"
                        class="align-self-stretch align-self-sm-auto"
                        @click="borrarDatosItem(item)"
                      >
                        Borrar datos
                      </b-button>
                    </b-card-header>
                    <b-card-body class="pt-3 pb-3">
                      <b-row>
                        <b-col cols="12">
                          <TextComponent
                            :form="item"
                            name="tratamiento"
                            :uid="fieldUid('tratamiento', gIndex, index)"
                            type="text"
                            rules="required|max:100"
                            label="Padecimiento o Tratamiento"
                          />
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                            type="date"
                            :form="item"
                            name="fechaInicio"
                            :uid="fieldUid('fechaInicio', gIndex, index)"
                            label="Fecha inicio"
                            rules="required|date_min:01/01/1900,DD/MM/YYYY|date_max:today,YYYY-MM-DD"
                          />
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                            :key="fieldUid('fechaFin', gIndex, index) + '-' + ((item && item.fechaInicio) || '')"
                            type="date"
                            :form="item"
                            name="fechaFin"
                            :uid="fieldUid('fechaFin', gIndex, index)"
                            :rules="fechaFinRules(item)"
                            label="Fecha de terminación"
                          />
                        </b-col>
                        <b-col cols="12" md="12" lg="4">
                          <TextComponent
                            :form="item"
                            name="medicoHospital"
                            :uid="fieldUid('medicoHospital', gIndex, index)"
                            type="text"
                            rules="required|max:100"
                            label="Médico / Hospital"
                          />
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                </div>
              </b-card>
            </div>

            <b-alert
              v-if="hayAlgunSi && !todosDetallesCompletos && form.padecimientos.length"
              show
              variant="warning"
              class="mt-2"
            >
              Complete padecimiento/tratamiento, fecha de inicio y médico / hospital en cada fila indicada.
            </b-alert>

            <b-row align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import { PREGUNTAS_CUESTIONARIO_MEDICO } from "@/data/colombia/salud/Selects";
import TextComponent from "@/components/controls/TextComponent.vue";
import { formatDate } from "@/helpers/dateHelper";

function nombreCompletoPersona(persona) {
  return [persona.name, persona.lastName, persona.secondLastName]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
}

function obtenerMetaPregunta(codigoPregunta) {
  const pregunta = Number(codigoPregunta);
  if (!Number.isFinite(pregunta)) return null;
  return PREGUNTAS_CUESTIONARIO_MEDICO.find((p) => p.code === pregunta) || null;
}

export default {
  name: "FormColombiaSalud04",
  components: { TextComponent },
  data() {
    return { form: {} };
  },
  computed: {
    hayAlgunSi() {
      if (!this.form.dependientes || !Array.isArray(this.form.dependientes)) return false;
      return this.form.dependientes.some((p) => (p.respuestas || []).some((r) => r === "S"));
    },
    detallesPorSolicitante() {
      const list = this.form.padecimientos || [];
      const byName = {};
      list.filter(Boolean).forEach((item) => {
        const nom = (item.nombreSolicitante || "").trim() || "Solicitante";
        if (!byName[nom]) byName[nom] = { nombreSolicitante: nom, items: [] };
        byName[nom].items.push(item);
      });
      return Object.values(byName);
    },
    todosDetallesCompletos() {
      if (!this.form.padecimientos || !this.form.padecimientos.length) return true;
      return this.form.padecimientos.every(
        (d) =>
          (d.tratamiento || "").trim() !== "" &&
          (d.nombreSolicitante || "").trim() !== "" &&
          (d.fechaInicio || "").trim() !== "" &&
          (d.medicoHospital || "").trim() !== ""
      );
    },
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const form = this.$store.state.wizard.form;
    if (form === undefined || form === null || form === "") {
      this.$router.push("/form/colombia/salud/");
      this.$store.dispatch("app/loading", false);
      return;
    }
    this.form = form;
    if (!Array.isArray(this.form.padecimientos)) this.form.padecimientos = [];

    if (this.form.detallesPadecimientos && this.form.detallesPadecimientos.length) {
      this.form.padecimientos = this.form.detallesPadecimientos.map((e) => ({
        pregunta: e.pregunta || "",
        nombreSolicitante: e.nombreSolicitante || "",
        tratamiento: e.padecimientoTratamiento || e.tratamiento || "",
        fechaInicio: e.fechaInicio || "",
        fechaFin: e.fechaFin || "",
        medicoHospital: e.medicoHospital || "",
      }));
      delete this.form.detallesPadecimientos;
    }

    const haySi = (this.form.dependientes || []).some((p) => (p.respuestas || []).some((r) => r === "S"));

    if (haySi) {
      const existentes = this.form.padecimientos;
      const list = [];
      (this.form.dependientes || []).forEach((persona) => {
        const resp = persona.respuestas || [];
        const nombreSolicitante = nombreCompletoPersona(persona) || persona.name || "";
        PREGUNTAS_CUESTIONARIO_MEDICO.filter((p) => p.type === "sino").forEach((meta) => {
          const r = resp[meta.pdfRow];
          if (r === "S") {
            const previo = existentes.find(
              (e) => (e.nombreSolicitante || "") === nombreSolicitante && Number(e.pregunta) === meta.code
            );
            list.push({
              pregunta: String(meta.code),
              nombreSolicitante,
              tratamiento: previo ? previo.tratamiento || "" : "",
              fechaInicio: previo ? previo.fechaInicio || "" : "",
              fechaFin: previo ? previo.fechaFin || "" : "",
              medicoHospital: previo ? previo.medicoHospital || "" : "",
            });
          }
        });
      });
      this.form.padecimientos = list.length
        ? list
        : [
            {
              pregunta: "",
              nombreSolicitante: "",
              tratamiento: "",
              fechaInicio: "",
              fechaFin: "",
              medicoHospital: "",
            },
          ];
    } else {
      this.form.padecimientos = [];
    }

    this.form.padecimientos.forEach((item) => {
      const meta = obtenerMetaPregunta(item.pregunta);
      if (meta) {
        item.pregunta = String(meta.code);
      }
    });
    this.$store.dispatch("app/loading", false);
  },
  methods: {
    formatDate,
    fieldUid(baseName, gIndex, index) {
      return `${baseName}-${gIndex}-${index}`;
    },
    fechaFinRules(item) {
      const fechaInicio = (item && item.fechaInicio) || "";
      if (!fechaInicio) return "";
      return `date_min:${formatDate(fechaInicio, "YYYY-MM-DD", "DD/MM/YYYY")},DD/MM/YYYY`;
    },
    textoPregunta(item) {
      if (!item) return "";
      const meta = obtenerMetaPregunta(item.pregunta);
      return meta ? meta.label : "";
    },
    detalleFaltante() {
      if (!this.hayAlgunSi || !Array.isArray(this.form.padecimientos)) return null;
      return this.form.padecimientos.find(
        (item) =>
          !(item.nombreSolicitante || "").trim() ||
          !(item.tratamiento || "").trim() ||
          !(item.fechaInicio || "").trim() ||
          !(item.medicoHospital || "").trim()
      );
    },
    detalleConFechasInvalidas() {
      if (!this.hayAlgunSi || !Array.isArray(this.form.padecimientos)) return null;
      const hoy = new Date().toISOString().slice(0, 10);
      return this.form.padecimientos.find((item) => {
        const fechaInicio = (item.fechaInicio || "").trim();
        const fechaFin = (item.fechaFin || "").trim();
        if (!fechaInicio) return false;
        if (fechaInicio < "1900-01-01" || fechaInicio > hoy) return true;
        return !!fechaFin && fechaFin < fechaInicio;
      });
    },
    previous() {
      this.$store
        .dispatch("wizard/save", { index: 3, form: this.form })
        .then(() => this.$router.push("/form/colombia-salud-3.html"));
    },
    borrarDatosItem(item) {
      if (!item) return;
      item.tratamiento = "";
      item.fechaInicio = "";
      item.fechaFin = "";
      item.medicoHospital = "";
    },
    next() {
      if (this.hayAlgunSi && !this.todosDetallesCompletos) {
        this.$bvToast.toast(
          "Complete nombre del solicitante, padecimiento/tratamiento, " +
            " fecha de inicio y médico / hospital en cada fila.",
          {
            title: "Validación",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      if (this.detalleFaltante()) {
        this.$bvToast.toast(
          "Complete nombre del solicitante, padecimiento/tratamiento, " +
            "fecha de inicio y médico / hospital en cada fila.",
          {
            title: "Validación",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      if (this.detalleConFechasInvalidas()) {
        this.$bvToast.toast(
          "Verifique las fechas: la fecha de inicio debe ser válida y la fecha de terminación no puede ser anterior.",
          {
            title: "Validación",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      this.$store.dispatch("wizard/save", { index: 4, form: this.form }).then(() => {
        if (this.form.actionCode.includes("CO203")) {
          this.$router.push("/form/colombia-salud-6.html");
        } else {
          this.$router.push("/form/colombia-salud-5.html");
        }
      });
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
}
.persona-asegurar-card .persona-asegurar-inner {
  border-radius: 0.35rem;
}
.persona-asegurar-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.detalle-solicitante-card .card-header {
  background-color: rgba(0, 39, 118, 0.08);
  font-weight: 600;
}
.detalle-solicitante-card .card-header h6 {
  color: var(--one);
}
</style>
